import type {
  FiParam,
  FiParamOption,
  FiParamVisibility,
  FiScopeRequest,
} from "@mapmycustomers/shared/types/entity/fieldInsight";

import Scope from "@app/types/dashboard/Scope";

// Transitional fallback: legacy BE seeds shipped before `headerBinding` was added on FiParam.
const LEGACY_HEADER_KEYS: Record<string, "end" | "start"> = {
  endDate: "end",
  startDate: "start",
};

const resolveHeaderBinding = (param: FiParam): "end" | "start" | undefined => {
  if (param.type !== "date") {
    return undefined;
  }
  return param.headerBinding ?? LEGACY_HEADER_KEYS[param.key];
};

export const isHeaderDateParam = (param: FiParam): boolean =>
  resolveHeaderBinding(param) !== undefined;

// Inline (non-header) date params: use the declared default, else leave empty.
const inferDateDefault = (param: FiParam): string =>
  typeof param.default === "string" && param.default ? param.default : "";

const pickFirstMiddleLast = (options: FiParamOption[]): Array<number | string> => {
  if (options.length === 0) {
    return [];
  }
  const indices = Array.from(
    new Set([0, Math.floor((options.length - 1) / 2), options.length - 1])
  );
  return indices.map((index) => options[index].value);
};

const isEmptyValue = (value: unknown): boolean => {
  if (value === null || value === undefined) {
    return true;
  }
  if (typeof value === "string") {
    return value.trim() === "";
  }
  if (Array.isArray(value)) {
    return value.length === 0;
  }
  return false;
};

/**
 * Seed the form state for a chart's params. Header date params get the bounds of
 * the period selected for the whole section (start vs end is metadata-driven via
 * `headerBinding`), never the chart's own date-param defaults; inline date params
 * use their declared default (empty when none). When no default ships, single
 * selects fall back to their first option and required multiselects to a
 * first/middle/last spread; optional multiselects stay empty.
 */
const initDefaultParams = (
  config: FiParam[],
  headerStart?: string,
  headerEnd?: string
): Record<string, unknown> => {
  const result: Record<string, unknown> = {};
  config.forEach((param) => {
    if (param.type === "date") {
      const binding = resolveHeaderBinding(param);
      if (binding) {
        result[param.key] = binding === "end" ? headerEnd : headerStart;
      } else {
        result[param.key] = inferDateDefault(param);
      }
      return;
    }
    if (param.type === "multiselect") {
      result[param.key] = Array.isArray(param.default)
        ? param.default
        : param.required
          ? pickFirstMiddleLast(param.options ?? [])
          : [];
      return;
    }
    if (param.type === "select") {
      result[param.key] =
        param.default ?? param.options?.[0]?.value ?? (param.required ? undefined : "");
      return;
    }
    if (param.type === "number") {
      result[param.key] = param.default ?? null;
      return;
    }
    result[param.key] = param.default ?? "";
  });
  return result;
};

export const resolveFiScopeRequest = (scope: Scope): FiScopeRequest => {
  if (scope.userId) {
    return { scope: "user", userId: scope.userId };
  }
  return scope.teamId ? { scope: "team", teamId: scope.teamId } : { scope: "org" };
};

export const fiScopeKey = (request: FiScopeRequest): string => {
  if (request.scope === "team") {
    return `team:${request.teamId}`;
  }
  if (request.scope === "user") {
    return `user:${request.userId}`;
  }
  return "org";
};

export const resolveParamScope = (scope: Scope): FiParamVisibility =>
  resolveFiScopeRequest(scope).scope;

// Seeded params predate `visibility` and are not backfilled, so absence means every scope.
export const isParamVisible = (param: FiParam, scope: FiParamVisibility): boolean =>
  !param.visibility || param.visibility.includes(scope);

// Values survive in state across a scope change, so a hidden param would keep filtering the chart.
export const omitHiddenParamValues = (
  config: FiParam[],
  params: Record<string, unknown>,
  scope: FiParamVisibility
): Record<string, unknown> => {
  const hidden = new Set(
    config.filter((param) => !isParamVisible(param, scope)).map((param) => param.key)
  );
  return hidden.size === 0
    ? params
    : Object.fromEntries(Object.entries(params).filter(([key]) => !hidden.has(key)));
};

// An optional multiselect left with nothing offered is omitted entirely: the server then binds
// NULL (no filter) where an empty array would match nothing.
export const omitUnofferedOptionValues = (
  config: FiParam[],
  params: Record<string, unknown>
): Record<string, unknown> => {
  let next: null | Record<string, unknown> = null;
  config.forEach((param) => {
    if (param.type !== "multiselect" && param.type !== "select") {
      return;
    }
    if (!Array.isArray(param.options)) {
      return;
    }
    const source = next ?? params;
    if (!(param.key in source)) {
      return;
    }
    const offered = new Set(param.options.map((option) => option.value));
    const value = source[param.key];
    if (param.type === "select") {
      if (value == null || value === "" || offered.has(value as number | string)) {
        return;
      }
      next = { ...source };
      delete next[param.key];
      return;
    }
    if (!Array.isArray(value)) {
      return;
    }
    const offeredValues = value.filter((entry) => offered.has(entry as number | string));
    if (offeredValues.length === 0 && !param.required) {
      next = { ...source };
      delete next[param.key];
    } else if (offeredValues.length !== value.length) {
      next = { ...source, [param.key]: offeredValues };
    }
  });
  return next ?? params;
};

export const omitInapplicableParamValues = (
  config: FiParam[],
  params: Record<string, unknown>,
  scope: Scope | undefined
): Record<string, unknown> =>
  omitUnofferedOptionValues(
    config,
    scope ? omitHiddenParamValues(config, params, resolveParamScope(scope)) : params
  );

// Mirrors /execute (v1150): multiselect only, distinct count, falling back to `default`
// when the key is absent. Stricter on one point — a non-array counts as zero rather than
// being skipped, so a scalar from a deep link can't reach the server unchecked.
export const isMinOptionsUnmet = (param: FiParam, params: Record<string, unknown>): boolean => {
  if (param.type !== "multiselect" || !param.minOptions) {
    return false;
  }
  const value = params[param.key] ?? param.default;
  return (Array.isArray(value) ? new Set(value).size : 0) < param.minOptions;
};

// Counts the raw array, not distinct values, as /execute (v1171) does.
export const isMaxOptionsExceeded = (param: FiParam, params: Record<string, unknown>): boolean => {
  if (param.type !== "multiselect" || !param.maxOptions) {
    return false;
  }
  const value = params[param.key] ?? param.default;
  return Array.isArray(value) && value.length > param.maxOptions;
};

// Also covers a breached option minimum or maximum.
export const isRequiredParamEmpty = (param: FiParam, params: Record<string, unknown>): boolean =>
  (!!param.required && isEmptyValue(params[param.key])) ||
  isMinOptionsUnmet(param, params) ||
  isMaxOptionsExceeded(param, params);

// Scoped callers must pass their scope: a required param hidden at this scope has no field on screen
// to fill, so counting it would block Apply with no way out.
export const requiredParamsMissing = (
  config: FiParam[],
  params: Record<string, unknown>,
  scope?: Scope
): boolean => {
  const paramScope = scope && resolveParamScope(scope);
  return config.some(
    (param) =>
      (!paramScope || isParamVisible(param, paramScope)) && isRequiredParamEmpty(param, params)
  );
};

export default initDefaultParams;
